import { Apollo } from "apollo-angular";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";

import { Category, NotificationType, SubscriptionCount } from "../../apis/networkdashboard/graphql/graphql";
import { CimQueryImpactTypes, CimQueryStatuses } from "../../apis/networkdashboard/static/custom-types";
import { CIM_QUERY } from "../../apis/networkdashboard/static/graphql-queries/cim-notifications";
import { COUNT_QUERIES } from "../../apis/networkdashboard/static/graphql-queries/subscription-count";
import { ENV } from "../../app/app.runtime";
import { FooterComponent } from "../../components/common/footer/footer";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { CombinedServiceTileComponent } from "../../components/dashboard/combined-service-tile/combined-service-tile";
import { NotificationContainer } from "../../components/dashboard/notification-container/notification-container";
import { DialogOnboarding } from "../../components/dialogs/dialog-onboarding/dialog-onboarding";
import { UserSettings } from "../../components/models/UserSettings";
import { ApiHelper } from "../../helpers/apihelper";
import { ProductType } from "../../helpers/enums/productType";
import { AuthService } from "../../services/authservice";
import { DashboardTileComponent } from "./components/dashboard-tile/dashboard-tile";
import { NewsContainer } from "./components/news-container/news-container";
import { SurfcertContainer } from "./components/surfcert-container/surfcert-container";
import { extractServices } from "./dashboard.functions";
import { generateEmptyTile } from "./tile-generator";

@Component({
  selector: "page-dashboard",
  templateUrl: "dashboard.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CombinedServiceTileComponent,
    DashboardTileComponent,
    FooterComponent,
    NavigationComponent,
    NewsContainer,
    NotificationContainer,
    SurfcertContainer,
  ],
})
export class DashboardPage {
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiHelper);
  private readonly apollo = inject(Apollo);
  private readonly dialog: MatDialog = inject(MatDialog);

  // Wired has no tile (IoTnet took its spot), but its page stays reachable
  // via /diensten/Wired.
  private subscriptions = new Map<ProductType, any>([
    [ProductType.IP, {}],
    [ProductType.LightPath, {}],
    [ProductType.L2VPN, {}],
    [ProductType.L3VPN, {}],
    [ProductType.Firewall, {}],
    [ProductType.Wireless, {}],
    [ProductType.Port, {}],
    [ProductType.IPPrefixes, {}],
    [ProductType.SURFdomeinen, {}],
    [ProductType.IoTnet, {}],
  ]);

  private productTypes = [
    ProductType.IP,
    ProductType.LightPath,
    ProductType.L2VPN,
    ProductType.L3VPN,
    ProductType.Firewall,
    ProductType.Wireless,
    ProductType.Port,
    ProductType.IPPrefixes,
  ];

  private topProductTypes = [
    ProductType.IP,
    ProductType.L3VPN,
    ProductType.Port,
    ProductType.L2VPN,
    ProductType.IPPrefixes,
    ProductType.LightPath,
  ];

  private sideProductTypes = [ProductType.SURFdomeinen];
  // IOTNET_HIDDEN only hides the tile; the /diensten/iotnet page stays reachable.
  private bottomProductTypes = [
    ProductType.Firewall,
    ProductType.Wireless,
    ...(ENV.IOTNET_HIDDEN ? [] : [ProductType.IoTnet]),
  ];

  protected readonly ipPrefixesAvailable = signal<boolean | undefined>(undefined);

  private readonly cimResource = resource({
    params: () => this.auth.viewingCustomer().customerId || undefined,
    loader: ({ params: customerId }) =>
      firstValueFrom(
        this.apollo.query<{ notifications: Array<NotificationType> }>({
          query: CIM_QUERY,
          fetchPolicy: "network-only",
          variables: {
            filter: {
              customerId,
              statuses: [CimQueryStatuses.OPEN, CimQueryStatuses.UPDATED],
              impacts: [
                CimQueryImpactTypes.DOWN,
                CimQueryImpactTypes.REDUCED_REDUNDANCY,
                CimQueryImpactTypes.RESILIENCE_LOSS,
              ],
            },
          },
        }),
      )
        .then(({ data }) => (data?.notifications ?? []).slice().sort((a, b) => a.startDate - b.startDate))
        .catch(() => []),
  });

  constructor() {
    this.auth.userLoaded.pipe(takeUntilDestroyed()).subscribe(() => {
      this.initDashboard();
      this.enableSurfDomeinen();
      this.enableIotNet();
    });
  }

  protected readonly customerActivities = computed(() =>
    (this.cimResource.value() ?? []).filter((note) => note.category === Category.Malfunction),
  );

  protected readonly customerNotifications = computed(() =>
    (this.cimResource.value() ?? []).filter((note) => note.category !== Category.Malfunction),
  );

  protected get topSubscriptions() {
    return new Map([...this.subscriptions].filter(([key]) => this.topProductTypes.includes(key)));
  }

  protected get sideSubscriptions() {
    return extractServices(this.sideProductTypes, this.subscriptions);
  }

  protected get bottomSubscriptions() {
    return extractServices(this.bottomProductTypes, this.subscriptions);
  }

  private initDashboard() {
    // disabled due to outdated info in Onboarding
    // this.maybeShowOnboarding();
    const hasRoles = (this.auth.currentUser().roles || []).length > 0;
    const hasTeams = (this.auth.currentUser().teams || []).length > 0;
    if (hasRoles || hasTeams) {
      this.fetchSubscriptions().catch(() => this.emptyTiles());
    } else {
      this.emptyTiles();
    }
  }

  /**
   * Show onboarding dialog if necessary.
   */
  private maybeShowOnboarding() {
    // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
    this.api.settings().then((settings: UserSettings) => {
      if (!settings.onboarding) {
        this.showOnBoarding();
      }
    });
  }

  private emptyTiles() {
    this.productTypes.forEach((p) => {
      this.subscriptions.set(p, generateEmptyTile(p));
    });
  }

  private enableSurfDomeinen() {
    this.subscriptions.set(ProductType.SURFdomeinen, {
      available: false,
      loading: false,
      status: {
        "active-corero-filters": 0, // active corero filters
        "inactive-corero-filters": 2, // inactive corero filters
        "open-incidents": 0, // open incidents
      },
      productType: ProductType.SURFdomeinen,
      key: "domains",
      url: "domains",
      subscriptions: 0,
      subscriptionsIds: [],
      messages: [],
      actionButtonText: "Subscription.domains.ActionButton",
    });
  }

  private enableIotNet() {
    if (ENV.IOTNET_HIDDEN) {
      return;
    }

    this.subscriptions.set(ProductType.IoTnet, {
      available: false,
      loading: false,
      productType: ProductType.IoTnet,
      key: "iotnet",
      url: "iotnet",
      subscriptions: 0,
      subscriptionsIds: [],
      messages: [],
      actionButtonText: "Subscription.iotnet.ActionButton",
    });
  }

  private showOnBoarding() {
    const dialogRef = this.dialog.open(DialogOnboarding);
    dialogRef.componentInstance.done.subscribe(() => {
      this.api.updateSettings({ onboarding: true }).then(() => {});
    });
  }

  private fetchSubscriptionCount = async (productType: ProductType) => {
    const formatCount = (count: SubscriptionCount & { firewallsEnabled?: number }) => ({
      counts: {
        ok: count.ok,
        notraffic: (count.notraffic ?? 0) + (count.unknown ?? 0) + (count.pending ?? 0),
        malfunction: count.malfunction,
      },
      subscriptions: count.all,
      firewallsEnabled: count.firewallsEnabled,
    });

    const query = COUNT_QUERIES[productType];
    this.apollo
      .query<any>({
        query,
        fetchPolicy: "network-only",
        variables: { customerId: this.auth.viewingCustomer().customerId },
      })
      .subscribe((data) => {
        // @ts-ignore
        const counts: any = { ...Object.values(data.data.counts).filter((item) => typeof item === "object")[0] };

        if (counts.all) {
          this.subscriptions.set(productType, {
            ...formatCount(counts),
            loading: false,
            actionButtonText: "Global.SeeOverview",
          });
        } else {
          this.subscriptions.set(productType, generateEmptyTile(productType));
        }

        if (productType === ProductType.IPPrefixes) {
          this.ipPrefixesAvailable.set(!!counts.all);
        }
      });
  };

  private fetchSubscriptions = async () => {
    this.productTypes.forEach((type) => type in COUNT_QUERIES && this.fetchSubscriptionCount(type));
  };
}
